import type { Adjustments } from './PriceSimulation.types';
import type { CalculatedPlan, Calculations, WorksheetFilters } from './PriceSimulationWorksheet.types';
import { isTiered } from './priceSimulationModel';
import { seedAdjustments } from './seedAdjustments';
import type { PackageSimulationDto } from './priceSimulationModel';
import { sumRounded } from '../../Utils/decimalUtils';
import { calculateWorksheet, currentAnnualValue, flattenPlans, isPlanHandled, VOLUME_THRESHOLD } from './worksheetCalculations';
import { transformPackageToWorksheet } from './worksheetDataTransformer';
import type { PackageWorksheet } from './worksheetDataTransformer';

export interface RoundProgress {
    done: number;
    total: number;
    currentAnnualValue: number;
    handledAnnualValue: number;
}

const NO_FILTERS: WorksheetFilters = { modificationStatuses: [], selectedPlans: [], selectedProductTypes: [], selectedProducts: [], onlyPlansWithVolume: false };

const hasNoVolume = (plan: CalculatedPlan): boolean => plan.quantity !== null && plan.quantity <= VOLUME_THRESHOLD;

const countsTowardsRound = (plan: CalculatedPlan): boolean => !hasNoVolume(plan);

const isPlanDone = (plan: CalculatedPlan): boolean => {
    if (plan.status !== 'Draft') return true;
    if (isTiered(plan)) return (plan.tierPoints ?? []).every((tp) => tp.delta !== 0 || tp.isKept);
    return isPlanHandled(plan);
};

const flatAnnualValue = (plan: CalculatedPlan): number => (isTiered(plan) ? 0 : currentAnnualValue(plan));

const toProgress = (plans: CalculatedPlan[]): RoundProgress => {
    const donePlans = plans.filter(isPlanDone);
    return {
        done: donePlans.length,
        total: plans.length,
        currentAnnualValue: sumRounded(plans.map(flatAnnualValue)),
        handledAnnualValue: sumRounded(donePlans.map(flatAnnualValue)),
    };
};

export const calculateFilteredProgress = (calculations: Calculations): RoundProgress => toProgress(flattenPlans(calculations).filter(countsTowardsRound));

export const calculateRoundProgress = (packageDetailsMap: Record<string, PackageSimulationDto>, adjustments: Adjustments): RoundProgress => {
    const worksheets = Object.entries(packageDetailsMap)
        .map(([packageId, dto]) => transformPackageToWorksheet(dto, packageId))
        .filter((worksheet): worksheet is PackageWorksheet => worksheet !== null);

    return toProgress(flattenPlans(calculateWorksheet(worksheets, seedAdjustments(adjustments, worksheets), NO_FILTERS)).filter(countsTowardsRound));
};
